// Layer: application. Depends on domain only.
//
// The retry policy for a first request that lands on a cold API, and the numbers behind
// it. Pure: no timers, no fetch, no clock. The caller walks the plan.
//
// Why this exists at all. storia-core runs on Cloud Run with no minimum instances and
// its database is a Neon compute that suspends when idle, so the first request after a
// quiet period waits for both to come back. The console used to abort that request at
// 10s and tell the operator STORIA was unreachable, which was false every single time:
// the service was starting, and it always arrived.
//
// ---------------------------------------------------------------- the measurement
// Four cold starts of revision storia-core-00009-4h4, read from the Cloud Run request
// log and the container's own startup log (1-2 October 2026). "Startup" is the gap
// between `Started server process` and `Application startup complete`, which is exactly
// `pool.open(wait=True)` plus the schema check, so it is the Neon resume:
//
//   when              container start    Neon resume + schema check    first response
//   02 Oct 15:59          14.70s                  0.70s                   15.23s
//   02 Oct 08:49          20.29s                  0.75s                   20.71s
//   02 Oct 12:54          20.85s                  0.70s                   21.24s
//   01 Oct 10:30          19.96s                  1.71s                   21.59s
//
//   warm requests (n=25): 0.12s - 1.12s
//
// Two things follow, and both shaped the numbers below.
//
// Neon is not the problem. It is 0.70-1.71s, three to eight per cent of the wait. The
// cost is the container image pull plus the Python import graph, 14.7-20.9s, and that is
// already with `startup-cpu-boost` on. Chasing the database would have bought nothing.
//
// The ceiling is not ours. Cloud Run's own per-request timeout on this service is 30s,
// so an attempt window above 30s is dead budget: the platform returns 504 first.
//
// ------------------------------------------------------------------- the budget
// 45s total, which is the worst measured cold start (21.6s) with room for a complete
// second attempt after the platform's 30s ceiling has had its say. It is deliberately
// not "enough for one cold start" — a budget with no margin over the slowest thing you
// measured will show the error panel the first time a slower one happens.
export const COLD_START_BUDGET_MS = 45_000;

// Show the waking state after this long, measured from the moment the FIRST attempt
// starts, not from a failure. A cold start answers the request rather than failing it,
// so a banner armed by the retry's catch branch only ever appeared once an attempt had
// timed out at 10s, and stayed silent through every cold start quicker than that.
//
// Above the slowest warm response measured (1.12s) by better than 2x, so a warm load
// never flashes a banner about cold starts.
export const WAKING_AFTER_MS = 2_500;

// Per-attempt windows, widening. The first is short because a genuine network error
// should surface fast and a retry costs nothing: aborting the client request does not
// cancel the container start it is queued behind, so the next attempt lands on the same
// warming instance. The last is wide enough to ride out a whole cold start on its own.
// Capped at Cloud Run's 30s request timeout, past which the wait is not ours to spend.
const ATTEMPT_TIMEOUTS_MS = [10_000, 18_000, 30_000] as const;

// Between attempts. Short, because the thing being waited for is measured in seconds
// and a long backoff would spend the budget on sleeping rather than on asking.
const BACKOFF_MS = [600, 1_200, 2_400] as const;

// Below this there is no point starting another attempt: it would abort before a warm
// response (1.12s measured) could arrive, and report a timeout that means nothing.
const MIN_ATTEMPT_MS = 2_000;

export interface Attempt {
  /** 1-based. */
  readonly number: number;
  /** How long to wait before starting this attempt. Zero for the first. */
  readonly delayMs: number;
  /** Abort this attempt after this long. Never exceeds the remaining budget. */
  readonly timeoutMs: number;
}

/**
 * The attempt to make after `elapsedMs` of trying, or null when the budget is spent.
 *
 * Driven by elapsed time rather than attempt count so that a slow attempt consumes the
 * budget it actually used. Counting attempts instead would let three slow ones run for
 * two minutes while the operator reads a banner promising "a few seconds".
 */
export function nextAttempt(attemptNumber: number, elapsedMs: number): Attempt | null {
  if (attemptNumber < 1) throw new Error("attemptNumber starts at 1");
  if (attemptNumber > ATTEMPT_TIMEOUTS_MS.length) return null;

  const delayMs = attemptNumber === 1 ? 0 : BACKOFF_MS[attemptNumber - 2];
  const remaining = COLD_START_BUDGET_MS - elapsedMs - delayMs;
  if (remaining < MIN_ATTEMPT_MS) return null;

  return {
    number: attemptNumber,
    delayMs,
    timeoutMs: Math.min(ATTEMPT_TIMEOUTS_MS[attemptNumber - 1], remaining),
  };
}

/**
 * Whether a failure is worth waiting through rather than reporting.
 *
 * Only `unavailable` — a timeout, a connection error, a 502/503 from the front end
 * while the container starts. Everything else is an answer: a rejected credential does
 * not improve on the second ask, and a 404 is not a cold start.
 */
export function isWaking(kind: string): boolean {
  return kind === "unavailable";
}
